<script setup lang="ts">
/**
 * Presentational list. Receives rows + pagination state + filter-count
 * + statusFilter via props; emits row-click / update:page /
 * filter-click / update:statusFilter / update:search. No queries, no
 * mutations, no router pushes — orchestration lives in the container.
 *
 * A status Select dropdown renders inside `#quick-filters` so the
 * operator can quickly narrow by Pending approval / Awaiting settlement
 * without opening the filter drawer.
 *
 * Title, subtitle, and search are passed as optional props so the
 * DataTableV2 header renders them inside the card chrome.
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Select from '@uiKit/elements/Select.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import { formatDate, formatMoney } from '@shared/utils'
import {
  getWithdrawStatusColor,
  getWithdrawStatusLabel,
  shortenId,
} from '../utils/withdrawRequestStatusUtils'
import {
  WITHDRAW_REQUEST_STATUS,
  type WithdrawRequestRow,
  type WithdrawRequestStatus,
} from '../types/withdrawRequestsTypes'
import type { SelectOption } from '@shared/types'

const props = defineProps<{
  rows: WithdrawRequestRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  filterCount: number
  emptyTitle?: string
  emptySubtitle?: string
  statusFilter: WithdrawRequestStatus | null
  title?: string
  subtitle?: string
  search?: string
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'row-click', row: WithdrawRequestRow): void
  (e: 'filter-click'): void
  (e: 'update:statusFilter', value: WithdrawRequestStatus | null): void
  (e: 'update:search', value: string): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: 'minmax(220px, 1.6fr)' },
  { key: 'amount', label: 'AMOUNT', width: 'minmax(160px, 1fr)', align: 'right' },
  { key: 'status', label: 'STATUS', width: 'minmax(170px, 1fr)' },
  { key: 'reference', label: 'EXTERNAL REF', width: 'minmax(180px, 1.2fr)' },
  { key: 'createdAt', label: 'REQUESTED AT', width: 'minmax(140px, 1fr)' },
]

const statusOptions = computed<SelectOption[]>(() => [
  { label: 'All statuses', value: '' },
  { label: 'Pending approval', value: WITHDRAW_REQUEST_STATUS.PENDING_APPROVAL },
  { label: 'Awaiting settlement', value: WITHDRAW_REQUEST_STATUS.AWAITING_SETTLEMENT },
])

const statusSelectModel = computed<string>({
  get: () => props.statusFilter ?? '',
  set: (value) => emit('update:statusFilter', (value as WithdrawRequestStatus) || null),
})

function formatAmount(row: WithdrawRequestRow): string {
  return formatMoney(row.currencyCode, row.amount)
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :title="title"
    :subtitle="subtitle"
    :search="search"
    search-placeholder="Search by investor name…"
    :local-search="false"
    :local-pagination="false"
    :filterable="true"
    :filter-count="filterCount"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :url-sync="true"
    page-size-storage-key="treasury.withdrawRequests.pageSize"
    clickable
    :empty-title="emptyTitle ?? 'No withdraw requests'"
    :empty-subtitle="emptySubtitle ?? 'Requests matching your filters will appear here.'"
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
    @update:search="emit('update:search', $event)"
    @filter-click="emit('filter-click')"
    @row-click="emit('row-click', $event as WithdrawRequestRow)"
  >
    <template #quick-filters>
      <Select
        v-model="statusSelectModel"
        :options="statusOptions"
        option-label="label"
        option-value="value"
        placeholder="All statuses"
        size="small"
        class="withdraw-requests-list__status-select"
      />
    </template>

    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{
          (row as WithdrawRequestRow).investorName || '—'
        }}</Text>
        <Text type="caption" color="muted">
          {{ (row as WithdrawRequestRow).investorEmail || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-amount="{ row }">
      <Text
        type="body-sm"
        weight="semibold"
        align="right"
        style="font-variant-numeric: tabular-nums"
      >
        {{ formatAmount(row as WithdrawRequestRow) }}
      </Text>
    </template>

    <template #cell-status="{ row }">
      <StatusBadge
        :color="getWithdrawStatusColor((row as WithdrawRequestRow).status)"
        :label="getWithdrawStatusLabel((row as WithdrawRequestRow).status)"
      />
    </template>

    <template #cell-reference="{ row }">
      <Box v-if="(row as WithdrawRequestRow).externalReferenceId" flex align="center" gap="2">
        <Text type="body-sm" color="muted">
          {{ shortenId((row as WithdrawRequestRow).externalReferenceId ?? '', 6, 4) }}
        </Text>
        <CopyButton
          :value="(row as WithdrawRequestRow).externalReferenceId ?? ''"
          label="Copy"
          copied-label="Copied"
        />
      </Box>
      <Text v-else type="body-sm" color="muted">—</Text>
    </template>

    <template #cell-createdAt="{ row }">
      <Text type="body-sm" color="muted">{{
        formatDate((row as WithdrawRequestRow).createdAt)
      }}</Text>
    </template>
  </DataTableV2>
</template>

<style scoped>
.withdraw-requests-list__status-select {
  min-width: 200px;
}
</style>
